import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';

import { ExpenseStatusControl } from 'molecules';

import {
  TCalendarDay,
  TCalendarRow,
  TONE,
  ToneDot,
  DRAG_KEY,
  IncomeStateChip,
  fmtMoney,
  isDraggableRow,
  useReschedule,
  weekDates,
} from '../shared';
import { splitDayRows, compactRows } from './agendaModel';
import { RowActionsMenu } from './RowActionsMenu';

export interface IPlanningBoardProps {
  /** ISO Monday of the visible week. */
  weekAnchor: string;
  /** Page the visible week (also fired by the drag edge-hover). */
  onWeekAnchorChange: (nextMondayIso: string) => void;
  /** Every fetched day (the shell fetches anchor week ± 1 week). */
  days: TCalendarDay[];
  /** Open a row in its edit surface. */
  onEdit: (row: TCalendarRow) => void;
  /** Delete a deletable row. */
  onDelete: (row: TCalendarRow) => void;
  /** Called after a successful reschedule (refetch). */
  onMoved?: () => void;
  /** Mark an unpaid expense row as paid. */
  onMarkPaid: (row: TCalendarRow) => void;
  /** Set an expense row to any status. */
  onSetStatus: (row: TCalendarRow, status: string) => void;
}

const DATE_FMT = 'YYYY-MM-DD';

/** How long a drag must hover an edge zone before the board pages a week. */
const EDGE_HOLD_MS = 600;

const rowKey = (row: TCalendarRow): string =>
  row.sourceRef || `${row.title}-${row.date}-${row.amountPln}`;

/**
 * The «Планирование» tab: a 7-column week board where every card is a payment
 * and dragging a card onto another day reschedules it (same mechanics and
 * mutation routing as the old WeekStrip — native HTML5 DnD, `DRAG_KEY`
 * sourceRef payload, `useReschedule`). Each column splits into «Придёт» /
 * «Уйдёт» zones with per-zone totals. Cross-week moves: drop a card onto the
 * ←/→ paging buttons (row date ± 7 days), or hover a board edge for 600 ms to
 * page the visible week while dragging (the fetch covers ± 1 week so the
 * dragged row survives the flip).
 */
export const PlanningBoard = memo<IPlanningBoardProps>(
  ({
    weekAnchor,
    onWeekAnchorChange,
    days,
    onEdit,
    onDelete,
    onMoved,
    onMarkPaid,
    onSetStatus,
  }) => {
    const { t } = useTranslation();
    const { reschedule } = useReschedule({ onDone: onMoved });

    const [dragging, setDragging] = useState(false);
    const [overDate, setOverDate] = useState<string | null>(null);
    const [overEdge, setOverEdge] = useState<'prev' | 'next' | null>(null);
    const edgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearEdgeTimer = useCallback(() => {
      if (edgeTimer.current) {
        clearTimeout(edgeTimer.current);
        edgeTimer.current = null;
      }
      setOverEdge(null);
    }, []);

    useEffect(() => clearEdgeTimer, [clearEdgeTimer]);

    const visibleDates = useMemo(() => weekDates(weekAnchor), [weekAnchor]);

    const byDate = useMemo(() => {
      const map: Record<string, TCalendarDay> = {};
      days.forEach((d) => {
        if (d.date) map[d.date] = d;
      });
      return map;
    }, [days]);

    // Index EVERY fetched row (± 1 week) so a drop after paging still resolves.
    const rowByRef = useMemo(() => {
      const map: Record<string, TCalendarRow> = {};
      days.forEach((d) => {
        [
          ...compactRows(d.incomeRows),
          ...compactRows(d.expenseRows),
          ...compactRows(d.receivedRows),
        ].forEach((r) => {
          if (r.sourceRef) map[r.sourceRef] = r;
        });
      });
      return map;
    }, [days]);

    const pageWeek = useCallback(
      (dir: -1 | 1) => {
        onWeekAnchorChange(
          moment(weekAnchor).add(dir, 'week').format(DATE_FMT),
        );
      },
      [weekAnchor, onWeekAnchorChange],
    );

    const onToday = useCallback(() => {
      onWeekAnchorChange(moment().startOf('isoWeek').format(DATE_FMT));
    }, [onWeekAnchorChange]);

    /* ----------------------------- drag wiring ----------------------------- */

    const onCardDragStart = useCallback(
      (row: TCalendarRow) => (e: React.DragEvent) => {
        if (!row.sourceRef) return;
        e.dataTransfer.setData(DRAG_KEY, row.sourceRef);
        e.dataTransfer.effectAllowed = 'move';
        setDragging(true);
      },
      [],
    );

    const onCardDragEnd = useCallback(() => {
      setDragging(false);
      setOverDate(null);
      clearEdgeTimer();
    }, [clearEdgeTimer]);

    const dropRow = useCallback(
      (e: React.DragEvent): TCalendarRow | undefined => {
        const ref = e.dataTransfer.getData(DRAG_KEY);
        return ref ? rowByRef[ref] : undefined;
      },
      [rowByRef],
    );

    const onColumnDragOver = useCallback(
      (date: string) => (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOverDate((d) => (d === date ? d : date));
      },
      [],
    );

    const onColumnDragLeave = useCallback(
      (date: string) => () => setOverDate((d) => (d === date ? null : d)),
      [],
    );

    const onColumnDrop = useCallback(
      (date: string) => (e: React.DragEvent) => {
        e.preventDefault();
        setOverDate(null);
        setDragging(false);
        clearEdgeTimer();
        const row = dropRow(e);
        if (row && row.date !== date) reschedule(row, date);
      },
      [dropRow, reschedule, clearEdgeTimer],
    );

    /** ←/→ buttons and edge zones: move the ROW one week back/forward. */
    const onWeekShiftDrop = useCallback(
      (dir: -1 | 1) => (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        clearEdgeTimer();
        const row = dropRow(e);
        if (row?.date) {
          reschedule(
            row,
            moment(row.date)
              .add(dir * 7, 'day')
              .format(DATE_FMT),
          );
        }
      },
      [dropRow, reschedule, clearEdgeTimer],
    );

    const allowDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }, []);

    /** Edge zones: hovering EDGE_HOLD_MS pages the visible week mid-drag. */
    const onEdgeDragOver = useCallback(
      (edge: 'prev' | 'next') => (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOverEdge(edge);
        if (!edgeTimer.current) {
          edgeTimer.current = setTimeout(() => {
            edgeTimer.current = null;
            setOverEdge(null);
            pageWeek(edge === 'prev' ? -1 : 1);
          }, EDGE_HOLD_MS);
        }
      },
      [pageWeek],
    );

    const onEdgeDragLeave = useCallback(() => clearEdgeTimer(), [
      clearEdgeTimer,
    ]);

    /* ------------------------------ rendering ------------------------------ */

    const periodCaption = useMemo(() => {
      const from = moment(weekAnchor);
      const to = moment(weekAnchor).add(6, 'day');
      if (from.isSame(to, 'month')) {
        return `${from.format('D')}–${to.format('D MMMM YYYY')}`;
      }
      return `${from.format('D MMMM')} – ${to.format('D MMMM YYYY')}`;
    }, [weekAnchor]);

    const renderCard = (row: TCalendarRow) => {
      const draggable = isDraggableRow(row);
      const primary = row.counterpartyName || row.title || '—';
      const secondary =
        row.invoiceNumber ||
        (row.counterpartyName && row.title !== row.counterpartyName
          ? row.title
          : null);
      const card = (
        <CardBox
          key={rowKey(row)}
          frozen={!draggable}
          draggable={draggable}
          onDragStart={draggable ? onCardDragStart(row) : undefined}
          onDragEnd={draggable ? onCardDragEnd : undefined}
        >
          {draggable && <span className="pb-handle">⠿</span>}
          <div className="pb-body">
            <span className="pb-primary" title={primary}>
              {primary}
            </span>
            {secondary ? (
              <span className="pb-secondary" title={secondary}>
                {secondary}
              </span>
            ) : null}
            <span className="pb-amount">{fmtMoney(row.amountPln)}</span>
            <span className="pb-chip">
              {row.direction === 'income' ? (
                <IncomeStateChip state={row.incomeState}>
                  {t(`pages.paymentCalendar.incomeStates.${row.incomeState}`)}
                </IncomeStateChip>
              ) : (
                <ExpenseStatusControl
                  value={row.status}
                  onChange={(s) => onSetStatus(row, s)}
                />
              )}
            </span>
          </div>
          <span className="pb-actions">
            <RowActionsMenu
              row={row}
              onEdit={onEdit}
              onDelete={onDelete}
              onMoved={onMoved}
              onMarkPaid={onMarkPaid}
            />
          </span>
        </CardBox>
      );
      return draggable ? (
        card
      ) : (
        <Tooltip
          key={rowKey(row)}
          title={t('pages.paymentCalendar.board.notDraggable')}
        >
          {card}
        </Tooltip>
      );
    };

    const renderZone = (side: 'in' | 'out', rows: TCalendarRow[]) => {
      const total = rows.reduce((sum, r) => sum + (r.amountPln || 0), 0);
      return (
        <Zone side={side}>
          <div className="pb-zone-head">
            <span>
              {side === 'in'
                ? t('pages.paymentCalendar.board.incoming')
                : t('pages.paymentCalendar.board.outgoing')}
            </span>
            <span className="pb-zone-sum">{fmtMoney(total)}</span>
          </div>
          {rows.length === 0 ? (
            <div className="pb-zone-empty" />
          ) : (
            rows.map(renderCard)
          )}
        </Zone>
      );
    };

    return (
      <Wrap>
        <Toolbar>
          <Button
            icon={<LeftOutlined />}
            onClick={() => pageWeek(-1)}
            onDragOver={allowDrop}
            onDrop={onWeekShiftDrop(-1)}
            title={t('pages.paymentCalendar.board.dropPrevWeek')}
            aria-label={t('pages.paymentCalendar.board.dropPrevWeek')}
          />
          <Button onClick={onToday}>
            {t('pages.paymentCalendar.nav.today')}
          </Button>
          <Button
            icon={<RightOutlined />}
            onClick={() => pageWeek(1)}
            onDragOver={allowDrop}
            onDrop={onWeekShiftDrop(1)}
            title={t('pages.paymentCalendar.board.dropNextWeek')}
            aria-label={t('pages.paymentCalendar.board.dropNextWeek')}
          />
          <span className="pb-period">{periodCaption}</span>
        </Toolbar>

        <BoardArea>
          <Columns>
            {visibleDates.map((date) => {
              const day = byDate[date];
              const { incoming, outgoing } = day
                ? splitDayRows(day)
                : { incoming: [], outgoing: [] };
              const end = day?.endBalancePln || 0;
              return (
                <DayColumn
                  key={date}
                  over={date === overDate}
                  onDragOver={onColumnDragOver(date)}
                  onDragLeave={onColumnDragLeave(date)}
                  onDrop={onColumnDrop(date)}
                >
                  <div className="pb-day-head">
                    <span>{moment(date).format('dd, DD.MM')}</span>
                    <ToneDot tone={day?.tone ?? end} />
                  </div>
                  {renderZone('in', incoming)}
                  {renderZone('out', outgoing)}
                </DayColumn>
              );
            })}
          </Columns>

          {dragging && (
            <>
              <EdgeZone
                side="left"
                active={overEdge === 'prev'}
                onDragOver={onEdgeDragOver('prev')}
                onDragLeave={onEdgeDragLeave}
                onDrop={onWeekShiftDrop(-1)}
              >
                ‹
              </EdgeZone>
              <EdgeZone
                side="right"
                active={overEdge === 'next'}
                onDragOver={onEdgeDragOver('next')}
                onDragLeave={onEdgeDragLeave}
                onDrop={onWeekShiftDrop(1)}
              >
                ›
              </EdgeZone>
            </>
          )}
        </BoardArea>

        <Hint>{t('pages.paymentCalendar.board.hint')}</Hint>
      </Wrap>
    );
  },
);

/* ----------------------------- styles ----------------------------- */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .pb-period {
    margin-left: 8px;
    font-size: 15px;
    font-weight: 600;
  }
`;

const BoardArea = styled.div`
  position: relative;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DayColumn = styled.div<{ over?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 220px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid ${({ over }) => (over ? '#1a1a1a' : '#e5e7eb')};
  background: ${({ over }) => (over ? '#f0f7ff' : '#fff')};
  transition: border-color 0.12s, background 0.12s;

  .pb-day-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 700;
    text-transform: capitalize;
  }
`;

const Zone = styled.div<{ side: 'in' | 'out' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 50%;

  .pb-zone-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${({ side }) => (side === 'in' ? TONE.ok : TONE.danger)};
  }
  .pb-zone-sum {
    font-variant-numeric: tabular-nums;
  }
  .pb-zone-empty {
    flex: 1 1 auto;
    min-height: 36px;
    border: 1px dashed #e2e8f0;
    border-radius: 8px;
  }
`;

const CardBox = styled.div<{ frozen?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #ededed;
  background: #fff;
  opacity: ${({ frozen }) => (frozen ? 0.55 : 1)};
  cursor: ${({ frozen }) => (frozen ? 'default' : 'grab')};

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }

  .pb-handle {
    flex: 0 0 auto;
    color: #94a3b8;
    font-size: 12px;
    line-height: 18px;
    cursor: grab;
  }
  .pb-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
  }
  .pb-primary {
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pb-secondary {
    color: #94a3b8;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pb-amount {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .pb-chip {
    display: inline-flex;
  }
  .pb-actions {
    flex: 0 0 auto;
  }
`;

const EdgeZone = styled.div<{ side: 'left' | 'right'; active?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ side }) => (side === 'left' ? 'left: -14px;' : 'right: -14px;')}
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 22px;
  font-weight: 700;
  color: ${({ active }) => (active ? '#1d4ed8' : '#94a3b8')};
  background: ${({ active }) =>
    active ? 'rgba(37, 99, 235, 0.18)' : 'rgba(148, 163, 184, 0.12)'};
  border: 1px dashed ${({ active }) => (active ? '#2563eb' : '#cbd5e1')};
  z-index: 2;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;
